import { supabase } from '../services/supabase';

export type FacilitiesGoogleGroupEvent = {
  id: string;
  title: string;
  description?: string | null;
  startDate: string;
  endDate: string;
  location?: string | null;
  type: string;
  currentAttendees?: number | null;
  maxAttendees?: number | null;
  isAllDay?: boolean | null;
  createdBy?: string | null;
  readOnly: true;
  source: 'google';
};

type FacilityBlackoutRow = {
  id: string;
  starts_at: string;
  ends_at: string;
  title?: string | null;
  reason?: string | null;
  location_of_event?: Array<string | null> | null;
  location_other?: string | null;
  all_day?: boolean | null;
};

const formatBlackoutLocation = (
  values?: Array<string | null | undefined> | null,
  locationOther?: string | null,
) => {
  const filtered =
    values?.map((value) => (typeof value === 'string' ? value.trim() : '')).filter(Boolean) ?? [];
  const otherText = locationOther?.trim() || '';

  if (filtered.length === 0) {
    return 'Campus-wide';
  }

  if (filtered.includes('Other') && otherText) {
    return `${filtered.join(', ')} (${otherText})`;
  }

  return filtered.join(', ');
};

const mapBlackoutToGroupEvent = (row: FacilityBlackoutRow): FacilitiesGoogleGroupEvent => ({
  id: `google-blackout-${row.id}`,
  title: String(row.title || '').trim() || 'Google Calendar Event',
  description: String(row.reason || '').trim() || null,
  startDate: String(row.starts_at || ''),
  endDate: String(row.ends_at || ''),
  location: formatBlackoutLocation(row.location_of_event, row.location_other),
  type: 'other',
  currentAttendees: null,
  maxAttendees: null,
  isAllDay: Boolean(row.all_day),
  createdBy: null,
  readOnly: true,
  source: 'google',
});

export const isFacilitiesGoogleCalendarEvent = (event: {
  readOnly?: boolean;
  source?: string;
  id?: string;
}) =>
  event.readOnly === true ||
  event.source === 'google' ||
  String(event.id || '').startsWith('google-blackout-');

export const mergeGroupCalendarEvents = <T extends { startDate: string }>(nativeEvents: T[], googleEvents: T[]) =>
  [...nativeEvents, ...googleEvents].sort(
    (left, right) => new Date(left.startDate).getTime() - new Date(right.startDate).getTime()
  );

export async function fetchFacilitiesGroupGoogleCalendarEvents(
  groupId: string,
): Promise<FacilitiesGoogleGroupEvent[]> {
  if (!groupId) return [];

  try {
    const { data: settings, error: settingsError } = await supabase
      .from('facility_settings')
      .select('facilities_group_id')
      .eq('id', 1)
      .maybeSingle();

    if (settingsError) {
      console.warn('Failed to load facility settings for group calendar overlay', settingsError);
      return [];
    }

    if (!settings?.facilities_group_id || settings.facilities_group_id !== groupId) {
      return [];
    }

    const startIso = new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString();
    const endIso = new Date(Date.now() + 730 * 24 * 60 * 60 * 1000).toISOString();

    const { data, error } = await supabase
      .from('facility_blackout_dates')
      .select('id, starts_at, ends_at, title, reason, location_of_event, location_other, all_day')
      .eq('source', 'google')
      .gt('ends_at', startIso)
      .lt('starts_at', endIso)
      .order('starts_at', { ascending: true });

    if (error) {
      console.warn('Failed to load Google-synced facility events', error);
      return [];
    }

    return ((data || []) as FacilityBlackoutRow[])
      .filter((row) => row?.id && row.starts_at && row.ends_at)
      .map(mapBlackoutToGroupEvent);
  } catch (error) {
    console.warn('Failed to overlay Google-synced facility events', error);
    return [];
  }
}
